import { formatFlightReviewDisplayMoney } from "./resolveFlightReviewDisplayMoney";

function asFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function asPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asPositiveNumberMany(...candidates) {
  for (const value of candidates) {
    const n = asPositiveNumber(value);
    if (n != null) return n;
  }
  return null;
}

function providerNativePerPersonAmount(offer) {
  const total = asPositiveNumberMany(
    offer?.providerPriceAmount,
    offer?.price?.amount,
    offer?.rawTotalFare,
    offer?.totalFare
  );
  if (total == null) return null;
  const pax = Math.max(1, Number(offer?.totalPaxCount) || 1);
  return total / pax;
}

function providerNativeCurrency(offer) {
  const raw =
    offer?.providerPriceCurrency ||
    offer?.price?.currency ||
    offer?.rawCurrency ||
    "";
  const c = String(raw).trim().toUpperCase();
  return c || null;
}

/**
 * SSOT for displayed card price + cheapest sort price in agent flight results.
 * Must remain aligned with FlightOfferResultCardLayout/FlightResultCard rendering rules.
 *
 * Priority:
 * 1) USD per passenger (hero in card when available)
 * 2) ILS per passenger (hero only when USD is missing)
 * 3) Total ILS (explicit fallback when per-pax is unavailable)
 *
 * @param {object} offer
 */
export function resolveAgentFlightDisplayedSortPrice(offer) {
  const usdPerPersonRaw = asPositiveNumber(offer?.priceClientPerPersonUsd);
  const ilsPerPersonRaw = asPositiveNumber(offer?.priceClientPerPersonIls);
  const ilsTotalRaw = asPositiveNumber(offer?.priceClientTotalIls);
  
  // המטבע המקורי מה-LDS - יכול להיות USD, EUR, GBP וכו'
  const originalCurrency = offer?.originalCurrency || offer?.rawCurrency || null;
  
  // DEBUG: תמיד הדפס כדי לראות
  if (process.env.NODE_ENV === "development" && (originalCurrency === "EUR" || originalCurrency === "GBP")) {
    console.log("[CURRENCY DEBUG - FORCED]", {
      offerId: offer?.id,
      rawCurrency: offer?.rawCurrency,
      originalCurrency: offer?.originalCurrency,
      finalCurrency: originalCurrency,
      usdPerPerson: usdPerPersonRaw,
      displayedUsd: usdPerPersonRaw != null ? Math.round(usdPerPersonRaw) : null,
    });
  }
  
  // DEBUG: Log for troubleshooting
  if (typeof window !== "undefined" && window.__HR_DEBUG_CURRENCY) {
    console.log("[CURRENCY DEBUG]", {
      offerId: offer?.id,
      rawCurrency: offer?.rawCurrency,
      originalCurrency: offer?.originalCurrency,
      finalCurrency: originalCurrency,
      usdPerPerson: usdPerPersonRaw,
    });
  }

  // Exact on-card formatting behavior.
  const displayedUsd = usdPerPersonRaw != null ? Math.round(usdPerPersonRaw) : null;
  const displayedIls = ilsPerPersonRaw != null ? Math.ceil(ilsPerPersonRaw) : null;
  const displayedTotalIls = ilsTotalRaw != null ? Math.ceil(ilsTotalRaw) : null;

  if (displayedUsd != null) {
    // זיהוי הסימן הנכון לפי המטבע המקורי
    const currencySymbol = 
      originalCurrency === "EUR" ? "€" :
      originalCurrency === "GBP" ? "£" :
      "$";  // ברירת מחדל USD
    
    const primaryLabel = `${currencySymbol}${displayedUsd}`;
    
    return {
      sortPrice: displayedUsd,
      sortPriceSource: "displayed_usd_per_person",
      cardPriceSource: "usd_per_person",
      displayedUsd,
      displayedIls,
      displayedTotalIls,
      isPrimaryCurrencyIls: false,
      priceUnit: "per_person",
      primaryPriceLabel: primaryLabel,  // ← חדש! עם הסימן הנכון
      originalCurrency,  // ← חדש!
    };
  }

  if (displayedIls != null) {
    return {
      sortPrice: displayedIls,
      sortPriceSource: "displayed_ils_per_person",
      cardPriceSource: "ils_per_person",
      displayedUsd,
      displayedIls,
      displayedTotalIls,
      isPrimaryCurrencyIls: true,
      priceUnit: "per_person",
      primaryPriceLabel: null,
      originalCurrency,  // ← חדש!
    };
  }

  if (displayedTotalIls != null) {
    return {
      sortPrice: displayedTotalIls,
      sortPriceSource: "displayed_ils_total",
      cardPriceSource: "ils_total",
      displayedUsd,
      displayedIls,
      displayedTotalIls,
      isPrimaryCurrencyIls: true,
      priceUnit: "total",
      primaryPriceLabel: null,
      originalCurrency,  // ← חדש!
    };
  }

  const nativePerPerson = providerNativePerPersonAmount(offer);
  const nativeCurrency = providerNativeCurrency(offer);

  if (nativePerPerson != null && nativeCurrency === "USD") {
    const nativeUsd = Math.round(nativePerPerson);
    return {
      sortPrice: nativeUsd,
      sortPriceSource: "provider_native_usd_per_person",
      cardPriceSource: "provider_native",
      displayedUsd: nativeUsd,
      displayedIls: null,
      displayedTotalIls: null,
      isPrimaryCurrencyIls: false,
      priceUnit: "per_person",
      primaryPriceLabel: null,
      originalCurrency: "USD",  // ← חדש!
    };
  }

  if (nativePerPerson != null && nativeCurrency === "ILS") {
    const nativeIls = Math.ceil(nativePerPerson);
    return {
      sortPrice: nativeIls,
      sortPriceSource: "provider_native_ils_per_person",
      cardPriceSource: "provider_native",
      displayedUsd: null,
      displayedIls: nativeIls,
      displayedTotalIls: null,
      isPrimaryCurrencyIls: true,
      priceUnit: "per_person",
      primaryPriceLabel: null,
      originalCurrency: "ILS",  // ← חדש!
    };
  }

  if (nativePerPerson != null && nativeCurrency) {
    const primaryPriceLabel = formatFlightReviewDisplayMoney(
      nativePerPerson,
      nativeCurrency
    );
    return {
      sortPrice: nativePerPerson,
      sortPriceSource: `provider_native_${nativeCurrency.toLowerCase()}`,
      cardPriceSource: "provider_native",
      displayedUsd: null,
      displayedIls: null,
      displayedTotalIls: null,
      isPrimaryCurrencyIls: false,
      priceUnit: "per_person",
      primaryPriceLabel,
      originalCurrency: nativeCurrency,  // ← חדש!
    };
  }

  return {
    sortPrice: null,
    sortPriceSource: "missing_display_price",
    cardPriceSource: "missing_display_price",
    displayedUsd: asFiniteNumber(offer?.priceClientPerPersonUsd),
    displayedIls: asFiniteNumber(offer?.priceClientPerPersonIls),
    displayedTotalIls: asFiniteNumber(offer?.priceClientTotalIls),
    isPrimaryCurrencyIls: false,
    priceUnit: "unknown",
    primaryPriceLabel: null,
    originalCurrency: originalCurrency || null,  // ← חדש!
  };
}
